/**
 * Background MUSIC: a shuffle of tracker-module tracks (public/music/), streamed through ONE reused
 * HTMLAudioElement — lazy HTTP streaming, one track at a time (never decoded into memory like the
 * WebAudio sfx engine). Fully separate from sfx, with its own gentle "music volume" (localStorage
 * "zmusicvol", capped by MUSIC_CEIL so it stays under the game sounds). Plays everywhere once a user
 * gesture unlocks audio; pauses on the Esc pause; dims during the elevator ride. Fires onNowPlaying
 * (→ the HUD credit toast) on each track. Credits live in the manifest (title + format + channels +
 * the YouTube url for the pause-menu link).
 */

export interface MusicTrack {
  file: string;     // "/music/NN.mp3" (served from public/)
  title: string;    // "Artist - Track"
  format: string;   // short module format: MOD / XM / IT / MPTM
  channels: number; // tracker channel count
  url: string;      // YouTube video — the credit link
  duration: number;
  formatLong?: string;
}

const MUSIC_CEIL = 0.55; // even at slider 100% the music stays gentle ("music shouldn't be loud")

class Music {
  private audio = new Audio();
  private tracks: MusicTrack[] = [];
  private order: number[] = []; // shuffled play order
  private pos = -1;
  private vol: number;   // user setting 0..1 (the slider)
  private dim = 1;       // transition dimmer (1 = full, 0 = faded out during the elevator ride)
  private loaded = false;
  private unlocked = false;
  private wantPlay = false; // start() called before the unlock gesture → begin on unlock
  private started = false;
  private fadeRAF = 0;
  /** Fired when a new track begins — the HUD shows the credit toast. */
  onNowPlaying: (t: MusicTrack) => void = () => {};

  constructor() {
    const saved = parseFloat(localStorage.getItem("zmusicvol") ?? "");
    this.vol = isNaN(saved) ? 0.5 : Math.max(0, Math.min(1, saved));
    this.audio.preload = "none";
    this.audio.addEventListener("ended", () => this.advance());
    this.audio.addEventListener("error", () => { if (this.started) this.advance(); }); // a bad track skips on
    this.apply();
  }

  private apply(): void {
    this.audio.volume = Math.max(0, Math.min(1, this.vol * MUSIC_CEIL * this.dim));
  }

  private async loadManifest(): Promise<void> {
    if (this.loaded) return;
    this.loaded = true;
    try {
      const m = (await fetch("/music/manifest.json").then((r) => r.json())) as MusicTrack[];
      this.tracks = Array.isArray(m) ? m : [];
      this.reshuffle();
    } catch {
      this.loaded = false; // allow a retry on the next start()
    }
  }

  private reshuffle(): void {
    this.order = this.tracks.map((_, i) => i);
    for (let i = this.order.length - 1; i > 0; i--) {
      const j = (Math.random() * (i + 1)) | 0;
      [this.order[i], this.order[j]] = [this.order[j], this.order[i]];
    }
  }

  /** Unlock audio on the first user gesture (the SAME one that resumes sfx). */
  unlock(): void {
    this.unlocked = true;
    if (this.wantPlay) { this.wantPlay = false; void this.begin(); }
  }

  /** Begin ambient playback. Idempotent (floors must not restart it); defers until the unlock gesture. */
  start(): void {
    if (this.started) return;
    if (!this.unlocked) { this.wantPlay = true; return; }
    void this.begin();
  }

  private async begin(): Promise<void> {
    if (this.started) return;
    this.started = true; // set synchronously to block re-entry across the await
    await this.loadManifest();
    if (!this.tracks.length) { this.started = false; return; }
    this.advance();
  }

  private advance(): void {
    if (!this.tracks.length) return;
    this.pos++;
    if (this.pos >= this.order.length) { this.pos = 0; this.reshuffle(); } // fresh shuffle each cycle
    const t = this.tracks[this.order[this.pos]];
    this.audio.src = t.file;
    this.dim = 1; this.apply();
    void this.audio.play().catch(() => {});
    this.onNowPlaying(t);
  }

  /** Skip to the next track. */
  next(): void { if (this.started) this.advance(); }

  pause(): void { this.audio.pause(); }
  resumePlayback(): void { if (this.started) void this.audio.play().catch(() => {}); }

  setVolume(v: number): void {
    this.vol = Math.max(0, Math.min(1, v));
    this.apply();
    try { localStorage.setItem("zmusicvol", String(this.vol)); } catch { /* ignore */ }
  }
  getVolume(): number { return this.vol; }

  /** Ramp the transition dimmer toward `target` (1 = full, 0 = silent) over `ms` — for the elevator ride. */
  fadeTo(target: number, ms: number): void {
    if (this.fadeRAF) cancelAnimationFrame(this.fadeRAF);
    const from = this.dim, to = Math.max(0, Math.min(1, target)), t0 = performance.now();
    const step = (now: number) => {
      const k = ms <= 0 ? 1 : Math.min(1, (now - t0) / ms);
      this.dim = from + (to - from) * k;
      this.apply();
      this.fadeRAF = k < 1 ? requestAnimationFrame(step) : 0;
    };
    this.fadeRAF = requestAnimationFrame(step);
  }

  currentTrack(): MusicTrack | undefined { return this.started && this.pos >= 0 ? this.tracks[this.order[this.pos]] : undefined; }
  allTracks(): MusicTrack[] { return this.tracks; }
}

export const music = new Music();
